import { Link } from "react-router-dom";
import { useEffect } from "react";
import useSeo from "../lib/useSeo";
import { Wrench, IdCard, ArrowRight, Percent, Receipt, TrendingUp, Fuel } from "lucide-react";

/**
 * Utility hub — index page listing every internal tool in a card grid.
 * New utilities: add an entry to `UTILITIES` below.
 */
const UTILITIES = [
  {
    slug: "rut",
    label: "Generador de RUT",
    description: "Genera RUT chilenos válidos y ficticios por rango de millones. Ideal para QA y demos.",
    icon: IdCard,
    to: "/utilidades/rut",
    available: true,
  },
  {
    slug: "iva",
    label: "Calculadora de IVA",
    description: "Calcula neto, IVA (19%) y bruto en segundos. Ingresa el neto o el bruto y obtén los tres valores.",
    icon: Percent,
    to: "/utilidades/iva",
    available: true,
  },
  {
    slug: "boleta",
    label: "Boleta de Honorarios",
    description: "Pasa de líquido a bruto o viceversa con la retención vigente 2026 (15,25%).",
    icon: Receipt,
    to: "/utilidades/boleta",
    available: true,
  },
  {
    slug: "ipc",
    label: "Calculadora de IPC",
    description: "Ajusta montos por inflación entre dos fechas y explora la variación histórica del IPC chileno.",
    icon: TrendingUp,
    to: "/utilidades/ipc",
    available: true,
  },
  {
    slug: "bencinas",
    label: "Bencinas Chile",
    description: "Compara precios de combustible por comuna con mapa, ranking de marcas y ahorro potencial. Datos oficiales CNE.",
    icon: Fuel,
    to: "/utilidades/bencinas",
    available: true,
  },
];

export default function UtilitiesHub() {
  useSeo({
    title: "Utilidades DoSoft · Herramientas para desarrolladores",
    description:
      "Colección de herramientas gratuitas para desarrolladores: generador de RUT chileno válido y más utilidades para QA, testing y demos.",
    canonical: "/utilidades",
  });

  // noindex so this internal hub doesn't compete with commercial pages in search results.
  useEffect(() => {
    let el = document.head.querySelector('meta[name="robots"]');
    const prev = el?.getAttribute("content");
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex,nofollow");
    return () => {
      if (prev) el.setAttribute("content", prev);
      else el.parentNode?.removeChild(el);
    };
  }, []);

  return (
    <div className="bg-ash-100 min-h-[70vh]">
      <main className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-brand-slate font-mono mb-1 flex items-center gap-2">
            <Wrench size={12} strokeWidth={1.8} /> Herramientas DoSoft
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight text-ink-900">
            Utilidades
          </h1>
          <p className="text-ink-800/70 text-sm md:text-base mt-3">
            Herramientas gratuitas creadas por DoSoft.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {UTILITIES.map((u) => (
            <UtilityCard key={u.slug} {...u} />
          ))}
        </div>
      </main>
    </div>
  );
}

function UtilityCard({ label, description, icon: Icon, to, available }) {
  const content = (
    <div
      className={`group h-full bg-white rounded-xl border border-ash-100/80 p-4 flex items-center gap-4 transition-all ${
        available
          ? "hover:border-brand hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
          : "opacity-60"
      }`}
    >
      <div
        className={`w-14 h-14 shrink-0 rounded-lg flex items-center justify-center transition-colors ${
          available
            ? "bg-brand/10 text-brand group-hover:bg-brand group-hover:text-white"
            : "bg-ash-100 text-ash-500"
        }`}
      >
        <Icon size={26} strokeWidth={1.7} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-display font-bold text-base text-ink-900 group-hover:text-brand-active transition-colors leading-tight truncate">
            {label}
          </h3>
          {!available && (
            <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-ash-100 text-ink-800/50 rounded shrink-0">
              Pronto
            </span>
          )}
        </div>
        <p className="text-xs text-ink-800/60 leading-snug">{description}</p>
      </div>
      {available && (
        <ArrowRight
          size={16}
          className="shrink-0 text-ash-500 group-hover:text-brand group-hover:translate-x-1 transition-all"
          strokeWidth={1.8}
        />
      )}
    </div>
  );

  if (!available) return content;
  return (
    <Link to={to} data-testid={`utility-card-${to.split("/").pop()}`} className="block h-full">
      {content}
    </Link>
  );
}
